import React, { useState } from 'react';
import PropTypes from 'prop-types';

const AnalyzedItemsDialog = ({ items, defaultLocation = 'pantry', onClose, onConfirm, existingCategories = [] }) => {
  const [editedItems, setEditedItems] = useState(items.map(item => ({
    name: item.name || '',
    quantity: item.quantity || '',
    unit: item.unit || '',
    category: item.category || '',
    location: item.location || defaultLocation,
    price: item.price || '',
  })));

  const handleItemChange = (index, field, value) => {
    const updated = [...editedItems];
    if (field === 'quantity' || field === 'price') {
      // Allow empty string for easier editing
      updated[index] = {
        ...updated[index],
        [field]: value === '' ? '' : (parseFloat(value) || ''),
      };
    } else {
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
    }
    setEditedItems(updated);
  };

  const handleRemove = (index) => {
    setEditedItems(editedItems.filter((_, i) => i !== index));
  };

  const handleConfirm = () => {
    // Convert empty strings to proper defaults before submitting
    const confirmItems = editedItems.map(item => ({
      ...item,
      quantity: item.quantity === '' ? 1 : (parseFloat(item.quantity) || 1),
      price: item.price === '' ? 0 : (parseFloat(item.price) || 0),
    }));
    onConfirm(confirmItems);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Review Analyzed Items
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Review and adjust the items found in your image before adding to pantry
          </p>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          <div className="space-y-4">
            {editedItems.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity *
                    </label>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit
                    </label>
                    <input
                      type="text"
                      value={item.unit}
                      onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder="lbs, oz, count"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location
                    </label>
                    <select
                      value={item.location}
                      onChange={(e) => handleItemChange(index, 'location', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="pantry">Pantry</option>
                      <option value="refrigerator">Refrigerator</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={item.category}
                        onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                        list={`category-options-${index}`}
                        placeholder="Type to search or enter new"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        autoComplete="off"
                      />
                      {existingCategories.length > 0 && (
                        <datalist id={`category-options-${index}`}>
                          {existingCategories.map((category) => (
                            <option key={category} value={category} />
                          ))}
                        </datalist>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price
                    </label>
                    <input
                      type="number"
                      value={item.price}
                      onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(index)}
                  className="mt-2 text-sm text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={editedItems.length === 0}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Add {editedItems.length} Item(s) to Pantry
          </button>
        </div>
      </div>
    </div>
  );
};

AnalyzedItemsDialog.propTypes = {
  items: PropTypes.array.isRequired,
  defaultLocation: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  existingCategories: PropTypes.arrayOf(PropTypes.string),
};

export default AnalyzedItemsDialog;

